import CheckIcon from "@mixitone/icons/check.svg";
import ApplicationController, { ApplicationView } from "@mixitone/mvc";
import { isNil } from "@mixitone/util";
import clsx from "clsx";
import React, { useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

type SegmentedInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "prefix" | "value" | "onChange"
> & {
  value?: string;
  onChange: (value: string) => void;
  error?: string;
  unstyled?: boolean;
  suffix?: string;
  prefix?: React.ReactNode;
  format: string; // Format pattern like "XXX-XXXX-XXXX"
  showValid?: boolean;
};

interface State {
  segmentLengths: number[];
  segments: string[];
  value: string;
}

class SegmentedInputController extends ApplicationController<State, SegmentedInputProps> {
  onChange: SegmentedInputProps["onChange"] = () => {};

  async changeProps(newProps: SegmentedInputProps): Promise<void> {
    const { format, value, onChange } = newProps;
    this.onChange = onChange;
    this.state.segmentLengths = format.split("-").map((segment) => segment.length);

    if (value !== this.state.value || isNil(this.state.segments)) {
      this.updateSegmentsFromValue(value || "");
    }
    this.state.value = value || "";
  }

  updateSegmentsFromValue(value: string) {
    const valueDigits = (value || "").replace(/\D/g, "");
    const segments: string[] = [];
    let currentIndex = 0;
    for (const length of this.state.segmentLengths) {
      segments.push(valueDigits.substring(currentIndex, currentIndex + length));
      currentIndex += length;
    }
    this.state.segments = segments;
  }

  actionUpdateSegment(index: number, segment: string) {
    this.state.segments[index] = segment;
    this.actionUpdateValueFromSegments();
  }

  actionUpdateValueFromSegments() {
    const segments = this.state.segments.map((segment) => segment.replace(/\D/g, ""));
    if (segments.every((segment, index) => segment.length === this.state.segmentLengths[index])) {
      this.state.value = segments.join("");
      this.onChange(this.state.value);
    }
  }

  actionPadSegment(index: number) {
    const segment = this.state.segments[index];
    const segmentLength = this.state.segmentLengths[index];
    if (segment.length < segmentLength) {
      this.state.segments[index] = segment.padStart(segmentLength, "0");
      this.actionUpdateValueFromSegments();
    }
  }

  actionUpdateValue(value: string) {
    this.state.value = value;
    this.updateSegmentsFromValue(value);
    this.actionUpdateValueFromSegments();
  }

  isSegmentFilled(index: number) {
    return this.state.segments[index].length === this.state.segmentLengths[index];
  }
}

/**
 * SegmentedInput component for entering formatted numeric values
 *
 * This component allows users to enter numbers that are visually
 * formatted into segments (e.g., XXX-XXXX-XXXX) while maintaining a single
 * string value internally.
 *
 * @example
 * ```tsx
 * // Basic usage
 * const [accountNumber, setAccountNumber] = useState("");
 *
 * <SegmentedInput
 *   value={accountNumber}
 *   onChange={setAccountNumber}
 *   format="XX-XXXX-XXXX"
 * />
 *
 * // With validation
 * <SegmentedInput
 *   value={accountNumber}
 *   onChange={setAccountNumber}
 *   format="XXX-XXXX-XXXX"
 *   error={accountNumber.length < 10 ? "Please enter a valid number" : undefined}
 *   showValid={accountNumber.length >= 10}
 * />
 * ```
 */
const ControlledSegmentedInput: React.FC<SegmentedInputProps> = ({
  format,
  error,
  unstyled,
  suffix,
  prefix,
  showValid,
  className,
  value: _value,
  onChange: _onChange,
  ...restProps
}) => {
  const controller = SegmentedInputController.use();
  const { segments, segmentLengths } = controller.state;

  const inputRefs = useRef<HTMLInputElement[]>([]);
  const [focused, setFocused] = useState<number | null>(null);

  // Handle input change for a specific segment
  const handleSegmentChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    controller.actionUpdateSegment(index, e.target.value);
    if (controller.isSegmentFilled(index) && index < segmentLengths.length - 1) {
      // Move to next input when current segment is filled
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle paste event
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pastedText) return;
    controller.actionUpdateValue(pastedText);
  };

  // Handle key press for navigation between segments
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !segments[index] && index > 0) {
      // Move to previous input when backspace is pressed on an empty input
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowLeft" && (e.target as HTMLInputElement).selectionStart === 0) {
      // Move to previous input when left arrow is pressed at the beginning
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowRight") {
      const input = e.target as HTMLInputElement;
      if (input.selectionStart === input.value.length && index < segmentLengths.length - 1) {
        // Move to next input when right arrow is pressed at the end
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleBlur = (index: number) => {
    setFocused(null);
    controller.actionPadSegment(index);
  };

  const inputClassName = unstyled
    ? ""
    : twMerge(
        "mt-1 block rounded border border-gray-300 py-2 px-2",
        clsx({
          "px-0 pl-5 pr-2": prefix,
        }),
      );

  return (
    <>
      <div className="relative flex items-center">
        {prefix && <div className="absolute left-2 mt-1 text-sm text-gray-500">{prefix}</div>}
        <div className="flex w-full items-center">
          {segmentLengths.map((length, index) => (
            <React.Fragment key={index}>
              <input
                ref={(el) => {
                  if (el) inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={segments[index]}
                maxLength={length}
                className={twMerge(
                  "rounded border border-gray-300 px-2 py-2 text-center",
                  focused === index ? "border-blue-500 ring-1 ring-blue-500" : "",
                  inputClassName,
                  className,
                  restProps.disabled ? "cursor-not-allowed bg-gray-100 opacity-70" : "",
                )}
                style={{ width: `${length + 2}ch` }}
                placeholder={"X".repeat(length)}
                {...restProps}
                onPaste={handlePaste}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onFocus={() => setFocused(index)}
                onBlur={() => handleBlur(index)}
                onChange={(e) => handleSegmentChange(index, e)}
              />
              {index < segmentLengths.length - 1 && <span className="mx-1 text-gray-500">-</span>}
            </React.Fragment>
          ))}
        </div>
        {showValid && (
          <div className="absolute right-2 mt-1 peer-invalid:hidden">
            <div className="flex h-5 w-5 items-center justify-center rounded-full border border-green-700 bg-green-500">
              <CheckIcon className="h-2 w-2 text-white" />
            </div>
          </div>
        )}
        {suffix && <div className="absolute right-8 mt-1 text-sm text-gray-500">{suffix}</div>}
      </div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </>
  );
};

export const SegmentedInput = SegmentedInputController.scope(ApplicationView(ControlledSegmentedInput));
